import {
  FileTextOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Alert, Button, Tag, Tooltip } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { DocumentDataContext } from 'features/libra/document/DocumentDataContext'
import { LibraDocumentContext } from 'features/libra/document/LibraDocumentContext'
import { useDocumentProcessingReady } from 'features/libra/document/useDocumentProcessingReady'
import { stampArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import {
  ApmCitation,
  DocumentCitation,
  FieldValue,
  FieldValues,
  FieldValuesSchema,
  LlmCitation,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { PermissionsGate } from 'features/permissions'
import type { FC } from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_MODUS,
  RolodexPermissionsValues,
} from 'types'

/**
 * Returns true when the AI attempted to extract this field but came back with
 * a null value — meaning the information wasn't found in the available documents.
 * `aiExtracted` is explicitly set to `false` by `useGongAgentAutofill` for
 * these fields, and cleared when the user edits the field (edit handlers produce
 * a fresh `wrapFieldValue` wrapper that does not carry the flag forward).
 * Safe to call with undefined (returns false).
 */
export function isAiUnextracted(fieldValue: FieldValue | undefined): boolean {
  return fieldValue?.aiExtracted === false
}

export const AiAutofillAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-icon {
    color: ${cantileverTheme.colors.OceanBlue} !important;
  }
`

export const AiAutofillButton = styled(Button)`
  background-color: ${cantileverTheme.colors.OceanBlue} !important;
`

/**
 * Safely reads the citation from a field value's llmValue envelope.
 *
 * gong-agent-service wraps llmValue as `{ value, citation }`. Older records
 * or test fixtures may have llmValue as a plain scalar, so this helper handles
 * both shapes gracefully.
 */
export function extractLlmCitation(
  fieldValue: FieldValue | undefined
): LlmCitation | null {
  if (!fieldValue?.llmValue) return null
  const lv = fieldValue.llmValue
  if (typeof lv === 'object' && lv !== null && 'citation' in (lv as object)) {
    return (
      (lv as { value: unknown; citation: LlmCitation | null }).citation ?? null
    )
  }
  return null
}

function isDocumentCitation(c: LlmCitation): c is DocumentCitation {
  return c !== null && (c as DocumentCitation).doc !== 'APM component data'
}

function isApmCitation(c: LlmCitation): c is ApmCitation {
  return c !== null && (c as ApmCitation).doc === 'APM component data'
}

function shortDocName(doc: string): string {
  const base = doc.split('/').pop() ?? doc
  return base.length > 30 ? base.slice(0, 27) + '...' : base
}

const CitationTag = styled(Tag)`
  cursor: default;
  font-size: 11px;
  margin: 0;
`

/**
 * Matches a citation's `doc` (filename from the LLM) against the list of
 * documents in the Libra viewer. Tries exact match first, then suffix match
 * for paths where the LLM only sees the base filename.
 */
function findEntryId(
  docName: string,
  documentsByCategory: ReadonlyMap<
    string,
    ReadonlyArray<{
      entry_id: string
      filename?: string
      visible_name?: string
    }>
  >
): string | null {
  const needle = docName.toLowerCase()
  for (const docs of documentsByCategory.values()) {
    for (const doc of docs) {
      const candidates = [doc.filename, doc.visible_name].filter(
        Boolean
      ) as string[]
      for (const candidate of candidates) {
        const c = candidate.toLowerCase()
        if (
          c === needle ||
          c.endsWith('/' + needle) ||
          c.endsWith('\\' + needle)
        ) {
          return doc.entry_id
        }
      }
    }
  }
  return null
}

export const AiCitationChip: FC<{ fieldValue: FieldValue | undefined }> = ({
  fieldValue,
}) => {
  // Use useContext directly so this degrades gracefully outside providers.
  const libraDoc = useContext(LibraDocumentContext)
  const docData = useContext(DocumentDataContext)

  if (!fieldValue) return null

  const valueIsEmpty =
    fieldValue.value === null ||
    fieldValue.value === undefined ||
    (Array.isArray(fieldValue.value) && fieldValue.value.length === 0)
  if (valueIsEmpty) return null

  const citation = extractLlmCitation(fieldValue)

  if (citation !== null) {
    if (isDocumentCitation(citation)) {
      const label = [
        shortDocName(citation.doc),
        citation.page !== null ? `p.${citation.page}` : null,
      ]
        .filter(Boolean)
        .join(' - ')

      const tooltipContent = (
        <div style={{ maxWidth: 320 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {citation.doc}
            {citation.page !== null ? `, page ${citation.page}` : ''}
          </div>
          {citation.excerpt && (
            <div style={{ fontStyle: 'italic' }}>
              &quot;{citation.excerpt}&quot;
            </div>
          )}
        </div>
      )

      const entryId =
        libraDoc && docData
          ? findEntryId(citation.doc, docData.documentsByCategory)
          : null

      const handleClick = entryId
        ? () =>
            libraDoc!.setSelectedDocumentId(entryId, citation.page ?? undefined)
        : undefined

      return (
        <Tooltip title={tooltipContent}>
          <CitationTag
            icon={<FileTextOutlined />}
            color='blue'
            onClick={handleClick}
            style={{ cursor: handleClick ? 'pointer' : 'default' }}>
            {label}
          </CitationTag>
        </Tooltip>
      )
    }

    if (isApmCitation(citation)) {
      const tooltipContent = `APM: ${citation.component} > ${citation.field}${
        citation.excerpt ? ` - "${citation.excerpt}"` : ''
      }`
      return (
        <Tooltip title={tooltipContent}>
          <CitationTag color='purple'>APM - {citation.component}</CitationTag>
        </Tooltip>
      )
    }
  }

  if (fieldValue.aiExtracted === false) {
    const searched = fieldValue.documentsSearched ?? []
    const tooltipContent =
      searched.length > 0
        ? `Searched: ${searched.join(', ')}`
        : 'No relevant content found in available documents'
    return (
      <Tooltip title={tooltipContent}>
        <CitationTag color='warning'>Not in documents</CitationTag>
      </Tooltip>
    )
  }

  return null
}

/**
 * Calls POST /api/v1/assess/section on the gong-agent-service for the given
 * section, then hands the returned field values to `onFieldValues`.
 *
 * Callers supply `onFieldValues` so each step component can apply the results
 * in whatever way suits its own state management.
 *
 * `inputs` carries optional per-request runtime parameters for execution
 * strategies that need more than the ticket id — e.g. zone suggestion passes
 * `{ zone_reference_image_id }`. Forwarded verbatim in the POST body.
 * NB: `inputs` participates in `autofill`'s identity by reference — pass a
 * stable (memoized) object, not an inline literal, or `autofill` is
 * re-created every render.
 */
export function useGongAgentAutofill({
  sectionId,
  onFieldValues,
  inputs,
}: {
  sectionId: string
  // The second arg carries response-level metadata (e.g. the documents the
  // model drew on) so callers can surface it without re-reading hook state
  // that isn't updated yet when this fires. Optional — callers that don't
  // need it (the autofill banner reads `documentsUsed` off the hook) ignore it.
  onFieldValues: (
    fieldValues: FieldValues,
    meta: { documentsUsed: string[] }
  ) => void
  inputs?: Record<string, unknown>
}) {
  const { GONG_AGENT_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ticketId } = useTicketAccess()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [documentsUsed, setDocumentsUsed] = useState<string[]>([])
  const [extractedAny, setExtractedAny] = useState<boolean | null>(null)

  const autofill = useCallback(async () => {
    if (!GONG_AGENT_URL) {
      setError('GONG_AGENT_URL is not configured')
      return
    }
    setLoading(true)
    setError(null)
    setDocumentsUsed([])
    setExtractedAny(null)
    try {
      const response = await axios.post(
        `${GONG_AGENT_URL}/api/v1/assess/section`,
        {
          ticket_id: ticketId,
          section_id: sectionId,
          ...(inputs && { inputs }),
        }
      )
      const fieldValues = FieldValuesSchema.parse(
        response.data?.field_values ?? {}
      )
      const stamped = stampArrayRowIds(fieldValues)
      const documentsFromResponse: string[] =
        response.data?.documents_used ?? []
      const markedFieldValues = Object.fromEntries(
        Object.entries(stamped).map(([k, fv]) => [
          k,
          fv.value === null || fv.value === undefined
            ? {
                ...fv,
                aiExtracted: false,
                documentsSearched: documentsFromResponse,
              }
            : fv,
        ])
      )
      onFieldValues(markedFieldValues, {
        documentsUsed: documentsFromResponse,
      })
      setDocumentsUsed(documentsFromResponse)
      setExtractedAny(
        Object.values(markedFieldValues).some(
          (fv) =>
            fv.aiExtracted !== false &&
            !(Array.isArray(fv.value) && fv.value.length === 0)
        )
      )
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'AI autofill failed'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [GONG_AGENT_URL, axios, ticketId, sectionId, onFieldValues, inputs])

  return { autofill, loading, error, documentsUsed, extractedAny }
}

export const AiAutofillBanner: FC<{
  section: { id: string; agentPermissions?: string[] }
  onFieldValues: (fieldValues: FieldValues) => void
}> = ({ section, onFieldValues }) => {
  const { autofill, loading, error, documentsUsed, extractedAny } =
    useGongAgentAutofill({
      sectionId: section.id,
      onFieldValues,
    })
  const { allProcessed, isCheckingProcessing, unprocessedCount } =
    useDocumentProcessingReady()

  const docsNotReady = !allProcessed && !isCheckingProcessing

  const description = useMemo(() => {
    if (error) return error
    if (documentsUsed.length > 0)
      return extractedAny
        ? undefined
        : 'Documents were found but no data could be extracted. Fields need to be filled manually.'
    if (extractedAny === false)
      return 'No relevant documents were found. Please ensure Documents are uploaded and processed.'
    if (docsNotReady)
      return `${unprocessedCount} document${unprocessedCount === 1 ? '' : 's'} still processing. Auto-fill will be available once all documents are processed.`
    return undefined
  }, [error, documentsUsed, extractedAny, docsNotReady, unprocessedCount])

  const alertType = useMemo(() => {
    if (error) return 'error' as const
    if (documentsUsed.length > 0)
      return extractedAny ? ('success' as const) : ('warning' as const)
    if (extractedAny === false) return 'warning' as const
    if (docsNotReady) return 'warning' as const
    return 'info' as const
  }, [error, documentsUsed, extractedAny, docsNotReady])

  const buttonDisabled = docsNotReady || isCheckingProcessing

  return (
    <PermissionsGate
      requires={[
        PERMISSION_GECKO_INTERNAL_MEMBER,
        PERMISSION_MODUS,
        ...((section.agentPermissions as RolodexPermissionsValues[]) ?? []),
      ]}>
      <AiAutofillAlert
        icon={loading ? <LoadingOutlined spin /> : <ThunderboltOutlined />}
        showIcon
        type={alertType}
        message='AI Auto-fill'
        description={description}
        action={
          !loading && (
            <Tooltip
              title={
                buttonDisabled
                  ? 'Documents are still being processed'
                  : undefined
              }>
              <AiAutofillButton
                type='primary'
                size='small'
                icon={<ThunderboltOutlined />}
                onClick={autofill}
                disabled={buttonDisabled}>
                Auto-fill
              </AiAutofillButton>
            </Tooltip>
          )
        }
      />
    </PermissionsGate>
  )
}
